import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { DevTool } from '@hookform/devtools';
import { useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import AdvancedSection from './advanced-section';
import BasicSection from './basic-section';
import PermissionsSection from './permissions-section';
import { ArticleFormValues, Menu, SIDE_MENU, CreateAs } from './types';

const BUTTON_NAME = {
  SETTINGS: 'Save',
  DRAFT: 'Save as Draft',
  DRAFT_EPISODE: 'Save and Preview',
  PUBLISH: 'Publish',
};

export interface SettingModalProps {
  contentID?: string;
  showCoverUpload?: boolean;
  showCopyright?: boolean;
  isOpen: boolean;
  actionType: CreateAs;
  onClose: () => void;
  methods: UseFormMethods<ArticleFormValues>;
  onSubmit: () => void;
  loading?: boolean;
  saveText?: string;
}

interface ModalSettingsProps extends SettingModalProps {
  section: string;
  onSectionChange: (section: string) => void;
}

export function ModalSettings({
  contentID: projectId,
  isOpen,
  actionType,
  onClose,
  section,
  methods,
  onSubmit,
  loading,
  showCoverUpload,
  showCopyright,
  onSectionChange,
  saveText,
}: ModalSettingsProps) {
  return (
    <Modal size="xl" autoFocus={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay>
        <ModalContent maxWidth="800px">
          <DevTool control={methods.control} />
          <ModalBody p={6} bg="gray.50" borderRadius="4px">
            <form onSubmit={onSubmit}>
              <Stack direction="row" spacing={9}>
                <Stack spacing={5}>
                  {Object.keys(SIDE_MENU).map((key: Menu) => (
                    <Heading
                      id={key}
                      key={key}
                      fontSize="xl"
                      cursor="pointer"
                      color={
                        section === SIDE_MENU[key] ? 'gray.900' : 'gray.300'
                      }
                      transition="all 0.3s ease-out"
                      onClick={() => onSectionChange(SIDE_MENU[key])}
                    >
                      {SIDE_MENU[key]}
                    </Heading>
                  ))}
                </Stack>

                {/* Basic */}
                <BasicSection
                  showCoverUpload={showCoverUpload}
                  contentID={projectId}
                  section={section}
                  register={methods.register}
                />

                {/* Advanced */}
                <AdvancedSection section={section} control={methods.control} />
                {/* Permissions */}
                <PermissionsSection
                  showCopyright={showCopyright}
                  section={section}
                  control={methods.control}
                />
              </Stack>

              <Stack
                spacing={2}
                direction="row"
                justifyContent="flex-end"
                mt={5}
              >
                <Button
                  type="button"
                  rounded="ellipse"
                  variant="ghost"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type={actionType !== CreateAs.SETTINGS ? 'submit' : 'button'}
                  rounded="ellipse"
                  isDisabled={loading}
                  onClick={() => {
                    if (actionType === CreateAs.SETTINGS) {
                      onClose();
                    }
                  }}
                >
                  {saveText || BUTTON_NAME[actionType]}
                </Button>
              </Stack>
            </form>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
}

function SettingModal(props: SettingModalProps) {
  const [section, setSection] = useState(SIDE_MENU.BASIC);
  return (
    <ModalSettings
      section={section}
      onSectionChange={setSection}
      onClose={() => {
        setSection(SIDE_MENU.BASIC);
        props.onClose();
      }}
      {...props}
    />
  );
}

export default SettingModal;
